import { type FC, type KeyboardEvent, useMemo } from 'react'
import classNames from 'classnames'
import { getExampleNumber } from 'libphonenumber-js/max'
import examples from 'libphonenumber-js/examples.mobile.json'
import { isCountryCode } from 'shared/lib'
import { Dropdown, type IDropdownItem } from 'shared/ui/Dropdown'
import { Icon } from 'shared/ui/Icon'
import { Typography } from 'shared/ui/Typography'
import { Error } from 'shared/ui/Error'
import { type ITooltipProps } from 'shared/ui/Tooltip'
import styles from './styles.module.scss'
import {
  type IInputComponent,
  type IInputPatternProps,
  makeInputPattern,
  makeTextFieldElement,
  makeTextFieldHighlight,
  makeTextFieldIcon,
  TextField,
  TextFieldPlaceholder,
} from '../TextField'
import { Label } from '../Label'
import { CountryEnum } from './types'

const generateDynamicFormat = (value: string) => {
  const cleanedValue = value?.replace(/\D/g, '')
  return cleanedValue?.length >= 7 ? '(###) ###-####' : '(###) ### ####'
}

const InputPhoneDefault = makeInputPattern({
  format: '(###) ###-####',
  placeholder: '(555) 800-8000',
  generateDynamicFormat,
})

const isValidCountryEnum = (id: string | number): id is CountryEnum => {
  if (typeof id !== 'string') return false

  const validCountries: string[] = Object.values(CountryEnum)

  return validCountries.includes(id)
}

const InputPhoneByCountry: Record<CountryEnum, IInputComponent<Partial<IInputPatternProps>>> = {
  [CountryEnum.GB]: makeInputPattern({
    format: '#### ######',
    placeholder: '5555 800800',
  }),

  [CountryEnum.AU]: makeInputPattern({
    format: '### ### ###',
    placeholder: '412 345 678',
  }),

  [CountryEnum.US]: InputPhoneDefault,

  [CountryEnum.CA]: InputPhoneDefault,

  [CountryEnum.World]: InputPhoneDefault,
}

type IPhoneWithLabelInputProps = {
  items: IDropdownItem[]
  number: string
  loading?: boolean
  numberError?: string | null
  disabled?: boolean
  hideError?: boolean
  item: IDropdownItem
  numberRequestError?: string | null
  isValidNumber?: boolean
  label?: string
  questionTooltipProps?: ITooltipProps
  triggerNumberValidation?: () => void
  setNumber?: (value: string) => void
  setCountry?: (item: IDropdownItem, child?: IDropdownItem) => void
  hideLabel?: boolean
  readOnly?: boolean
  isNoMarginTopInputLabel?: boolean
  withSearch?: boolean
  searchPlaceholder?: string
  dropdownWidth?: number
  constantHighlight?: boolean
}

export const PhoneWithLabelInput: FC<IPhoneWithLabelInputProps> = ({
  items,
  numberError,
  numberRequestError,
  number,
  loading = false,
  item,
  label = 'Mobile phone number',
  isValidNumber,
  questionTooltipProps,
  setCountry,
  setNumber,
  triggerNumberValidation,
  hideLabel = false,
  hideError = false,
  disabled = false,
  readOnly = false,
  isNoMarginTopInputLabel,
  withSearch = false,
  searchPlaceholder = 'Search',
  dropdownWidth,
  constantHighlight = false,
}) => {
  const onKeyDownNumber = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      triggerNumberValidation?.()
      if (isValidNumber && document.activeElement instanceof HTMLElement)
        document.activeElement.blur()
    }
  }

  const Input = useMemo(() => {
    if (isValidCountryEnum(item.id)) return InputPhoneByCountry[item.id]
    if (isCountryCode(item.id)) {
      const example = getExampleNumber(item.id, examples)
      if (example) {
        const callingCode = `+${example.countryCallingCode}`
        const international = example.formatInternational()
        const subscriber = international.startsWith(callingCode)
          ? international.slice(callingCode.length).replace(/^[\s\-]+/, '')
          : ''
        if (subscriber) {
          return makeInputPattern({
            format: subscriber.replace(/\d/g, '#'),
            placeholder: subscriber,
          })
        }
      }
    }
    return InputPhoneDefault
  }, [item.id])

  return (
    <>
      {!hideLabel && (
        <Label
          label={{
            text: label,
            className: classNames(styles.inputLabel, {
              [styles.noMarginTop]: isNoMarginTopInputLabel,
            }),
          }}
          questionTooltipProps={questionTooltipProps}
        />
      )}
      <TextField
        className={classNames(styles.textField, {
          [styles.error]: (numberError || numberRequestError) && !hideError,
        })}
        Input={Input}
        InputProps={{ onKeyDown: onKeyDownNumber }}
        error={!!numberRequestError}
        variant='stroke'
        size='medium'
        value={number}
        disabled={loading || disabled}
        onChange={setNumber}
        onBlur={triggerNumberValidation}
        readOnly={readOnly}
        leftActions={[
          makeTextFieldElement({
            element: (
              <div className={styles.leftContentNumber}>
                <Dropdown
                  placement={'bottom-start'}
                  width={dropdownWidth ?? 195}
                  margin={-28}
                  marginLeft={-12}
                  disabled={disabled || readOnly}
                  items={items}
                  onChange={setCountry}
                  withSearch={withSearch}
                  placeholder={searchPlaceholder}
                  triggerComponent={() => (
                    <TextFieldPlaceholder
                      rightActions={[
                        makeTextFieldIcon({
                          icon: 'chevronDown',
                        }),
                      ]}
                      disabled={loading || disabled}
                      placeholder={item.iconL ? <Icon icon={item.iconL} /> : item.customIconL}
                      variant={'integrated'}
                      size={'small'}
                      className={styles.countryPicker}
                    />
                  )}
                />
                <div className={styles.verticalDivider}></div>
                <Typography variant={'text-input-field'} ariaLabel={'numberPrefix'}>
                  {item.labelRight}
                </Typography>
              </div>
            ),
          }),
        ]}
        mainActions={[
          makeTextFieldHighlight({
            mode: 'error',
            highlight: !!numberError,
            constantHighlight,
          }),
        ]}
      />
      {!hideError && <Error type={'text'} error={numberError || numberRequestError} />}
    </>
  )
}
